import { createUseStyles } from 'react-jss';
import color from 'color';

import { flex } from '@resources/mixins';
import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    modal: {
        width: 'min(480px, 100%)',
    },

    header: {
        marginBottom: theme.spacing(6),
    },

    title: {
        color: theme.colors.text_heading,
        fontSize: 22,
        lineHeight: 1.3,
        fontWeight: 600,
        fontFamily: theme.font.secondary,
        margin: 0,
    },

    subtitle: {
        color: theme.colors.grey_text,
        marginTop: theme.spacing(2),
    },

    // ---- code input ----

    codeRow: {
        ...flex('row', 'center', 'center'),
        gap: theme.spacing(2),
        margin: `${theme.spacing(8)} 0 ${theme.spacing(2)}`,
    },

    // Reserves its height whether or not the button is there, so the dialog does not jump the
    // moment the first character is typed.
    clearRow: {
        ...flex('row', 'center', 'center'),
        minHeight: 28,
        marginBottom: theme.spacing(4),
    },

    clearButton: {
        border: 'none',
        background: 'none',
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
        borderRadius: theme.radius,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.primary,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'underline',

        '&:hover': {
            backgroundColor: theme.colors.transp_violet,
        },

        '&:disabled': {
            opacity: 0.5,
            cursor: 'default',
        },
    },

    codeGap: {
        width: theme.spacing(4),
        [theme.down('mobile')]: {
            width: theme.spacing(2),
        },
    },

    codeBox: {
        width: 52,
        height: 64,
        padding: 0,
        textAlign: 'center',
        fontFamily: theme.font.secondary,
        fontSize: 28,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: theme.colors.text_heading,
        caretColor: theme.colors.violet_primary,
        backgroundColor: theme.colors.surface_alt,
        border: `1.5px solid ${theme.colors.border_subtle}`,
        borderRadius: theme.radius,
        outline: 'none',
        transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',

        '&:focus': {
            borderColor: theme.colors.violet_primary,
            backgroundColor: theme.colors.white,
            boxShadow: `0 0 0 3px ${color(theme.colors.violet_primary).fade(0.8)}`,
        },

        '&:disabled': {
            opacity: 0.6,
        },

        [theme.down('mobile')]: {
            width: 42,
            height: 54,
            fontSize: 22,
        },
    },

    codeBoxFilled: {
        backgroundColor: theme.colors.white,
        borderColor: theme.colors.grey_light,
    },

    codeBoxError: {
        borderColor: theme.colors.red,
    },

    // ---- device card ----

    deviceCard: {
        backgroundColor: theme.colors.surface_alt,
        border: `1.5px solid ${theme.colors.border_subtle}`,
        borderRadius: theme.radius,
        padding: theme.spacing(6),
        marginBottom: theme.spacing(6),
    },

    deviceName: {
        color: theme.colors.text_heading,
        fontSize: 18,
        fontWeight: 600,
        marginBottom: theme.spacing(4),
        wordBreak: 'break-word',
    },

    deviceRow: {
        ...flex('row', 'space-between'),
        gap: theme.spacing(4),
        padding: `${theme.spacing(1)} 0`,
    },

    deviceLabel: {
        color: theme.colors.grey_text,
    },

    deviceValue: {
        color: theme.colors.text_heading,
        textAlign: 'right',
        wordBreak: 'break-word',
    },

    warning: {
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(6),
    },

    // ---- who the device is for ----

    picker: {
        marginBottom: theme.spacing(6),
    },

    pickerLegend: {
        display: 'block',
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(2),
    },

    pickerToggle: {
        ...flex('row', 'flex-start', 'stretch'),
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: theme.colors.surface_alt,
        border: `1.5px solid ${theme.colors.border_subtle}`,
        borderRadius: theme.radius,
    },

    pickerToggleButton: {
        flex: 1,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        border: 'none',
        borderRadius: theme.radius,
        background: 'none',
        color: theme.colors.grey_text,
        fontFamily: theme.font.primary,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background .15s ease, color .15s ease',

        '&:hover': {
            color: theme.colors.text_heading,
        },
    },

    pickerToggleButtonActive: {
        backgroundColor: theme.colors.white,
        color: theme.colors.violet_primary,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    },

    pickerSearch: {
        ...flex('row', 'flex-start', 'center'),
        gap: theme.spacing(2),
        marginTop: theme.spacing(4),
        padding: `0 ${theme.spacing(3)}`,
        border: `1.5px solid ${theme.colors.border_subtle}`,
        borderRadius: theme.radius,
        backgroundColor: theme.colors.white,

        '&:focus-within': {
            borderColor: theme.colors.violet_primary,
        },
    },

    pickerSearchIcon: {
        color: theme.colors.grey_text,
        fontSize: 20,
    },

    pickerSearchInput: {
        flex: 1,
        padding: `${theme.spacing(3)} 0`,
        border: 'none',
        outline: 'none',
        background: 'none',
        color: theme.colors.text_heading,
        fontFamily: theme.font.primary,
        fontSize: 14,
    },

    // Capped and scrollable: the company list can be long, and the dialog must not grow past
    // the viewport on the phone somebody is holding next to the headset.
    pickerList: {
        listStyle: 'none',
        margin: `${theme.spacing(2)} 0 0`,
        padding: 0,
        maxHeight: 208,
        overflowY: 'auto',
        border: `1.5px solid ${theme.colors.border_subtle}`,
        borderRadius: theme.radius,
    },

    pickerOption: {
        ...flex('column', 'center', 'flex-start'),
        width: '100%',
        gap: 2,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: theme.font.primary,

        '&:hover': {
            backgroundColor: theme.colors.transp_violet,
        },
    },

    pickerOptionSelected: {
        backgroundColor: theme.colors.transp_violet,
        boxShadow: `inset 3px 0 0 ${theme.colors.violet_primary}`,
    },

    pickerOptionName: {
        color: theme.colors.text_heading,
        fontSize: 14,
        fontWeight: 600,
        wordBreak: 'break-word',
    },

    pickerOptionEmail: {
        color: theme.colors.grey_text,
        fontSize: 13,
        wordBreak: 'break-word',
    },

    pickerHint: {
        color: theme.colors.grey_text,
        marginTop: theme.spacing(3),
    },

    // ---- outcome ----

    outcome: {
        ...flex('column', 'center', 'center'),
        textAlign: 'center',
        padding: `${theme.spacing(4)} 0`,
    },

    outcomeIcon: {
        fontSize: 48,
        marginBottom: theme.spacing(4),
    },

    outcomeApproved: {
        color: theme.colors.green,
    },

    outcomeDenied: {
        color: theme.colors.red,
    },

    outcomeBody: {
        color: theme.colors.grey_text,
        marginTop: theme.spacing(2),
    },

    // ---- shared ----

    errorWrapper: {
        marginBottom: theme.spacing(6),
    },

    buttons: {
        ...flex('row', 'flex-end', 'center'),
        gap: theme.spacing(4),
        marginTop: theme.spacing(8),

        [theme.down('mobile')]: {
            ...flex('column-reverse', 'flex-start', 'stretch'),
            gap: theme.spacing(3),
        },
    },
}));
